import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  alpha
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  LocalOffer as LocalOfferIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Collections as CollectionsIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { fetchProducts, updateProduct, deleteProduct, getSupportedCurrencies, updateProductPrices } from '../productsApi';
import config from '../config';
import { useBranch } from '../BranchContext';

const Products = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedBranch } = useBranch();
  const navigate = useNavigate();
  
  const [editOpen, setEditOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currencyPrices, setCurrencyPrices] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  
  // Multi-image states
  const [productImages, setProductImages] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);
  // Product Colors state
  const [productColors, setProductColors] = useState([]);
  const [currentColor, setCurrentColor] = useState({ colorName: '', colorImage: null, colorImagePreview: null });
  
  // ✅ NEW: Product Sizes state
  const [productSizes, setProductSizes] = useState([]);
  const [currentSizeName, setCurrentSizeName] = useState('');

  const [helpOpen, setHelpOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const getProductImagesCount = (product) => {
    const productImages = product.productImages || product.ProductImages;
    if (!productImages) return 0;
    if (Array.isArray(productImages)) return productImages.length;
    return 0;
  };

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        setCurrencies(data.Currencies || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
        setCurrencies([
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
        ]);
      }
    };
    loadCurrencies();
  }, []);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadProducts = async () => {
    if (!selectedBranch) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchProducts(selectedBranch.id || selectedBranch.Id);
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      Swal.fire(t('error'), t('loadProductsError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };

  const getCurrencyInfo = (code) => {
    return currencies.find(c => c.code === code) || { code, symbol: code, name: code, decimals: 2 };
  };

  const handleAddCurrency = useCallback((currency) => {
    setCurrencyPrices(prev => {
      if (!prev[currency]) {
        return { ...prev, [currency]: '' };
      }
      return prev;
    });
  }, []);

  const handleRemoveCurrency = useCallback((currency) => {
    setCurrencyPrices(prev => {
      const updated = { ...prev };
      delete updated[currency];
      return updated;
    });
  }, []);

  const handlePriceChange = useCallback((currency, value) => {
    setCurrencyPrices(prev => ({
      ...prev,
      [currency]: value
    }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setCurrentProduct(prev => ({ ...prev, ImageJson: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Handle multiple ProductImages upload
  const handleProductImagesChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    
    if (productImagePreviews.length + files.length > 8) {
      Swal.fire(t('error'), t('maxImagesExceeded') || 'Maximum 8 additional images allowed', 'warning');
      return;
    }

    const newPreviews = [];
    const newImages = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        newImages.push(reader.result);
        
        if (newPreviews.length === files.length) {
          setProductImagePreviews(prev => [...prev, ...newPreviews]);
          setProductImages(prev => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  }, [productImagePreviews.length, t]);

  // Remove ProductImage
  const handleRemoveProductImage = useCallback((index) => {
    setProductImagePreviews(prev => prev.filter((_, i) => i !== index));
    setProductImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle color name change
  const handleColorNameChange = useCallback((e) => {
    setCurrentColor({
      ...currentColor,
      colorName: e.target.value
    });
  }, [currentColor]);

  // Handle color image upload
  const handleColorImageChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentColor({
          ...currentColor,
          colorImage: file,
          colorImagePreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  }, [currentColor]);

  // Add color to list
  const handleAddColor = useCallback(() => {
    if (!currentColor.colorName.trim()) {
      Swal.fire(t('error'), t('colorNameRequired') || 'Please enter a color name', 'warning');
      return;
    }

    if (productColors.some(c => c.colorName.toLowerCase() === currentColor.colorName.toLowerCase())) {
      Swal.fire(t('error'), t('colorAlreadyExists') || 'This color already exists', 'warning');
      return;
    }

    setProductColors([...productColors, { ...currentColor }]);
    setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
    
    Swal.fire({
      icon: 'success',
      title: t('success'),
      text: `${t('colorAdded') || 'Color added'}: ${currentColor.colorName}`,
      timer: 2000,
      showConfirmButton: false
    });
  }, [currentColor, productColors, t]);

  // Remove color from list
  const handleRemoveColor = useCallback((index) => {
    const removed = productColors[index];
    setProductColors(productColors.filter((_, i) => i !== index));
    
    Swal.fire({
      icon: 'info',
      title: t('removed'),
      text: `${t('colorRemoved') || 'Color removed'}: ${removed.colorName}`,
      timer: 2000,
      showConfirmButton: false
    });
  }, [productColors, t]);

  // ✅ NEW: Add size to list
  const handleAddSize = useCallback(() => {
    const name = currentSizeName.trim();
    if (!name) {
      Swal.fire(t('error'), t('sizeNameRequired') || 'Please enter a size name', 'warning');
      return;
    }

    if (productSizes.some(s => s.sizeName.toLowerCase() === name.toLowerCase())) {
      Swal.fire(t('error'), t('sizeAlreadyExists') || 'This size already exists', 'warning');
      return;
    }

    setProductSizes(prev => [...prev, { sizeName: name }]);
    setCurrentSizeName('');

    Swal.fire({
      icon: 'success',
      title: t('success'),
      text: `${t('sizeAdded') || 'Size added'}: ${name}`,
      timer: 2000,
      showConfirmButton: false
    });
  }, [currentSizeName, productSizes, t]);

  // ✅ NEW: Remove size from list
  const handleRemoveSize = useCallback((index) => {
    const removed = productSizes[index];
    setProductSizes(prev => prev.filter((_, i) => i !== index));

    Swal.fire({
      icon: 'info',
      title: t('removed'),
      text: `${t('sizeRemoved') || 'Size removed'}: ${removed.sizeName}`,
      timer: 2000,
      showConfirmButton: false
    });
  }, [productSizes, t]);

  const handleEditClick = async (product) => {
    try {
      const cat = product.categoryName || product.CategoryName || '';
      const isOffer = cat.startsWith('العروض:') || cat === 'Offer' || cat === 'Deal';
      const isGrouped = cat.startsWith('مجموعة:') || cat === 'Grouped Product';

      // Load existing ProductColors
      const existingProductColors = product.productColors || product.ProductColors || [];
      const colorsData = [];

      if (Array.isArray(existingProductColors) && existingProductColors.length > 0) {
        existingProductColors.forEach((color) => {
          let colorImageUrl = null;
          
          if (color.colorImage) {
            if (typeof color.colorImage === 'object' && color.colorImage !== null) {
              colorImageUrl = color.colorImage.large || color.colorImage.medium || color.colorImage.thumbnail;
            } else if (typeof color.colorImage === 'string') {
              colorImageUrl = color.colorImage.startsWith('http') 
                ? color.colorImage 
                : `${config.API_BASE_URL}${color.colorImage}`;
            }
          }
          
          colorsData.push({
            colorName: color.colorName,
            colorImage: null,
            colorImagePreview: colorImageUrl
          });
        });
      }

      setProductColors(colorsData);
      setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });

      // ✅ NEW: Load existing ProductSizes
      const existingProductSizes = product.productSizes || product.ProductSizes || [];
      const sizesData = [];

      if (Array.isArray(existingProductSizes) && existingProductSizes.length > 0) {
        existingProductSizes.forEach((size) => {
          sizesData.push({ sizeName: size.sizeName || size.SizeName || '' });
        });
      }

      setProductSizes(sizesData);
      setCurrentSizeName('');
      
      let type = 'normal';
      if (isOffer) type = 'offer';
      else if (isGrouped) type = 'grouped';

      let currentPrices = {};
      let oldPrices = {};
      
      if (product.CurrencyPrices && typeof product.CurrencyPrices === 'object') {
        currentPrices = product.CurrencyPrices;
      } else if (product.price && product.currency) {
        currentPrices = { [product.currency || product.Currency || 'YER']: product.price || product.Price };
      } else {
        currentPrices = { YER: product.price || product.Price };
      }

      if (product.OldCurrencyPrices && typeof product.OldCurrencyPrices === 'object') {
        oldPrices = product.OldCurrencyPrices;
      } else if (product.oldPrice || product.OldPrice) {
        const oldPrice = product.oldPrice || product.OldPrice;
        oldPrices = { [product.currency || product.Currency || 'YER']: oldPrice };
      }

      const firstCurrency = Object.keys(currentPrices)[0] || 'YER';
      const currentPrice = currentPrices[firstCurrency];
      const oldPrice = oldPrices[firstCurrency] || currentPrice;
      
      let discount = 0;
      if (oldPrice > currentPrice && oldPrice > 0) {
        discount = Math.round(((oldPrice - currentPrice) / oldPrice) * 100);
      }
      
      setCurrencyPrices(currentPrices);

      // Load existing ProductImages
      const existingProductImages = product.productImages || product.ProductImages || [];
      const imagePreviews = [];
      
      if (Array.isArray(existingProductImages) && existingProductImages.length > 0) {
        existingProductImages.forEach((img) => {
          let imgUrl = null;
          
          if (typeof img === 'object' && img !== null) {
            imgUrl = img.large || img.medium || img.thumbnail;
          } else if (typeof img === 'string') {
            imgUrl = img.startsWith('http') ? img : `${config.API_BASE_URL}${img}`;
          }
          
          if (imgUrl) {
            imagePreviews.push(imgUrl);
          }
        });
      }
      
      setProductImagePreviews(imagePreviews);
      setProductImages([]);

      setCurrentProduct({
        Id: product.id || product.Id,
        ProductName: product.productName || product.ProductName,
        Description: product.description || product.Description || '',
        Price: currentPrice,
        Currency: firstCurrency,
        OldPrice: oldPrice,
        Discount: discount,
        Ingredients: product.ingredients || product.Ingredients || '',
        IsActive: product.isActive !== undefined ? product.isActive : product.IsActive,
        CategoryId: product.categoryId || product.CategoryId,
        ImageJson: null,
        Type: type,
        CurrencyPrices: currentPrices,
        OldCurrencyPrices: oldPrices,
        ExistingProductImages: existingProductImages
      });
      
      let imgUrl = product.imageUrl || product.ImageUrl;

      if (typeof imgUrl === 'object' && imgUrl !== null && !Array.isArray(imgUrl)) {
        if (imgUrl.medium) {
          imgUrl = imgUrl.medium.startsWith('http') ? imgUrl.medium : `${config.API_BASE_URL}${imgUrl.medium}`;
        } else {
          imgUrl = 'https://via.placeholder.com/150';
        }
      } else if (typeof imgUrl === 'string') {
        if (imgUrl.startsWith('/images/')) {
          imgUrl = `${config.API_BASE_URL}${imgUrl}`;
        } else if (!imgUrl) {
          imgUrl = 'https://via.placeholder.com/150';
        }
      } else {
        imgUrl = 'https://via.placeholder.com/150';
      }

      setImagePreview(imgUrl);
      setEditOpen(true);
    } catch (error) {
      console.error(error);
      Swal.fire(t('error'), t('loadProductDetailsError'), 'error');
    }
  };

  const handlePriceChangeFromDiscount = useCallback((discountValue) => {
    setCurrentProduct(prev => {
      const discount = parseFloat(discountValue);
      
      if (isNaN(discount) || discount < 0 || discount > 100) {
        return { ...prev, Discount: discountValue };
      }

      const oldPrices = prev.OldCurrencyPrices || {};
      const newPrices = {};
      
      Object.keys(oldPrices).forEach(currency => {
        const oldPrice = parseFloat(oldPrices[currency]);
        if (!isNaN(oldPrice)) {
          newPrices[currency] = oldPrice * (1 - discount / 100);
        }
      });

      const firstCurrency = Object.keys(newPrices)[0] || prev.Currency;
      const newPrice = newPrices[firstCurrency];

      setCurrencyPrices(newPrices);
      
      return {
        ...prev,
        Discount: discountValue,
        Price: newPrice,
        CurrencyPrices: newPrices
      };
    });
  }, []);
    
  const handleSave = async () => {
    try {
      const productId = currentProduct.Id;

      if (currentProduct.Type === 'normal') {
        const validPrices = Object.entries(currencyPrices).filter(([_, price]) => price && parseFloat(price) > 0);
        
        if (validPrices.length === 0) {
          Swal.fire(t('error'), t('atLeastOnePriceRequired') || 'At least one price is required', 'error');
          return;
        }
        
        const finalCurrencyPrices = {};
        validPrices.forEach(([currency, price]) => {
          finalCurrencyPrices[currency] = parseFloat(price);
        });
        
        const payload = { ...currentProduct };
        payload.CurrencyPrices = finalCurrencyPrices;
        
        const firstCurrency = Object.keys(finalCurrencyPrices)[0];
        payload.Price = finalCurrencyPrices[firstCurrency];
        payload.Currency = firstCurrency;

        // Add ProductImages to payload
        if (productImages.length > 0) {
          payload.ProductImages = productImages;
        }

        // Process product colors
        if (productColors.length > 0) {
          const productColorsArray = [];
          
          for (const color of productColors) {
            let colorImageBase64 = null;
            
            if (color.colorImage) {
              const reader = new FileReader();
              colorImageBase64 = await new Promise((resolve) => {
                reader.onload = (e) => resolve(e.target.result);
                reader.readAsDataURL(color.colorImage);
              });
            } else if (color.colorImagePreview) {
              colorImageBase64 = color.colorImagePreview;
            }
            
            productColorsArray.push({
              colorName: color.colorName,
              colorImage: colorImageBase64
            });
          }
          
          payload.ProductColors = productColorsArray;
          console.log(`🎨 Updating ${productColorsArray.length} product colors`);
        } else {
          payload.ProductColors = null;
        }

        // ✅ NEW: Process product sizes
        if (productSizes.length > 0) {
          payload.ProductSizes = productSizes.map(s => ({ sizeName: s.sizeName }));
          console.log(`📏 Updating ${productSizes.length} product sizes`);
        } else {
          payload.ProductSizes = null;
        }
        
        await updateProduct(productId, payload);
        
      } else if (currentProduct.Type === 'offer' || currentProduct.Type === 'grouped') {
        const discount = parseFloat(currentProduct.Discount);
        
        if (isNaN(discount) || discount < 0 || discount > 100) {
          Swal.fire(t('error'), 'Invalid discount percentage', 'error');
          return;
        }

        const oldPrices = currentProduct.OldCurrencyPrices || currentProduct.CurrencyPrices || {};
        
        if (Object.keys(oldPrices).length === 0) {
          Swal.fire(t('error'), 'No price data found for this product', 'error');
          return;
        }

        const newPrices = {};
        Object.keys(oldPrices).forEach(currency => {
          const oldPrice = parseFloat(oldPrices[currency]);
          if (!isNaN(oldPrice)) {
            newPrices[currency] = oldPrice * (1 - discount / 100);
          }
        });

        await updateProductPrices(productId, {
          CurrencyPrices: newPrices,
          OldCurrencyPrices: oldPrices,
          Discount: discount
        });

        if (currentProduct.ImageJson) {
          await updateProduct(productId, {
            ImageJson: currentProduct.ImageJson
          });
        }
      }
      
      setEditOpen(false);
      setProductImages([]);
      setProductImagePreviews([]);
      setProductColors([]);
      setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
      setProductSizes([]); // ✅ NEW
      setCurrentSizeName(''); // ✅ NEW
      Swal.fire(t('success'), t('productUpdatedSuccess'), 'success');
      loadProducts();
      
    } catch (error) {
      console.error('Error updating product:', error);
      Swal.fire(t('error'), t('updateProductError') + ': ' + (error.response?.data?.ErrorMessage || error.message), 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t('areYouSure'),
      text: t('revertWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('yesDeleteIt'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        await deleteProduct(id);
        Swal.fire(t('deleted'), t('productDeleted'), 'success');
        loadProducts();
      } catch (error) {
        Swal.fire(t('error'), t('deleteProductError'), 'error');
      }
    }
  };

  const getProductType = (p) => {
    const cat = p.categoryName || p.CategoryName || '';
    if (cat.startsWith("مجموعة:") || cat === "Grouped Product") return 'grouped';
    if (cat.startsWith("العروض:") || cat === "Offer" || cat === "Deal") return 'offer';
    return 'normal';
  };

  const normalProducts = products.filter(p => getProductType(p) === 'normal');
  const offers = products.filter(p => getProductType(p) === 'offer');
  const groupedProducts = products.filter(p => getProductType(p) === 'grouped');

  const filterProducts = (productList) => {
    if (!searchQuery) return productList;
    return productList.filter(p => 
      (p.productName || p.ProductName || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const ProductCard = ({ product }) => {
    let imgUrl = product.imageUrl || product.ImageUrl;

    if (typeof imgUrl === 'object' && imgUrl !== null && !Array.isArray(imgUrl)) {
      if (imgUrl.medium) {
        imgUrl = imgUrl.medium.startsWith('http') ? imgUrl.medium : `${config.API_BASE_URL}${imgUrl.medium}`;
      } else {
        imgUrl = 'https://via.placeholder.com/150';
      }
    } else if (typeof imgUrl === 'string') {
      if (imgUrl.startsWith('/images/')) {
        imgUrl = `${config.API_BASE_URL}${imgUrl}`;
      } else if (!imgUrl) {
        imgUrl = 'https://via.placeholder.com/150';
      }
    } else {
      imgUrl = 'https://via.placeholder.com/150';
    }

    const hasSubProducts = Array.isArray(product.SubProducts) && product.SubProducts.length > 0;
    const isActive = product.isActive || product.IsActive;
    const currency = product.currency || product.Currency || 'YER';
    const currencySymbol = getCurrencySymbol(currency);
    const productImagesCount = getProductImagesCount(product);

    const getProductColorsCount = (product) => {
      const productColors = product.productColors || product.ProductColors;
      if (!productColors) return 0;
      if (Array.isArray(productColors)) return productColors.length;
      return 0;
    };

    // ✅ NEW: Get product sizes count for card badge
    const getProductSizesCount = (product) => {
      const productSizes = product.productSizes || product.ProductSizes;
      if (!productSizes) return 0;
      if (Array.isArray(productSizes)) return productSizes.length;
      return 0;
    };

    const cat = product.categoryName || product.CategoryName || '';
    let type = 'normal';
    if (cat.startsWith('مجموعة:') || cat === 'Grouped Product') type = 'grouped';
    else if (cat.startsWith('العروض:') || cat === 'Offer' || cat === 'Deal') type = 'offer';

    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Card 
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            borderRadius: { xs: 2, sm: 3 },
            '&:hover': {
              boxShadow: 6,
              transform: isMobile ? 'none' : 'translateY(-4px)'
            }
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height={isMobile ? "160" : "180"}
              image={imgUrl}
              alt={product.productName || product.ProductName}
              sx={{ objectFit: 'cover' }}
            />
            <Chip
              label={isActive ? t('active') : t('inactive')}
              color={isActive ? 'success' : 'error'}
              size="small"
              sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8,
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 22, sm: 24 }
              }}
            />
            <Stack 
              direction="row" 
              spacing={0.5} 
              sx={{ position: 'absolute', top: 8, left: 8 }}
            >
              <Chip
                label={currency}
                size="small"
                sx={{ 
                  bgcolor: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 22, sm: 24 }
                }}
              />
              {productImagesCount > 0 && (
                <Tooltip title={`${productImagesCount} ${t('additionalImages') || 'additional images'}`}>
                  <Chip
                    icon={<CollectionsIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'white' }} />}
                    label={productImagesCount}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.9),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: 22, sm: 24 },
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                </Tooltip>
              )}
              {getProductColorsCount(product) > 0 && (
                <Tooltip title={`${getProductColorsCount(product)} ${t('colors') || 'colors'}`}>
                  <Chip
                    label={`🎨 ${getProductColorsCount(product)}`}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.secondary.main, 0.9),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: 22, sm: 24 }
                    }}
                  />
                </Tooltip>
              )}
              {/* ✅ NEW: Sizes badge on product card */}
              {getProductSizesCount(product) > 0 && (
                <Tooltip title={`${getProductSizesCount(product)} ${t('sizes') || 'sizes'}`}>
                  <Chip
                    label={`📏 ${getProductSizesCount(product)}`}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.9),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: 22, sm: 24 }
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>

          <CardContent sx={{ flexGrow: 1, pb: 1, p: { xs: 1.5, sm: 2 } }}>
            <Typography 
              gutterBottom 
              variant="h6" 
              component="div" 
              noWrap
              sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
            >
              {product.productName || product.ProductName}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                height: { xs: 35, sm: 40 }, 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.8rem', sm: '0.85rem' }
              }}
            >
              {product.description || product.Description}
            </Typography>
            <Typography 
              variant="h6" 
              color="primary" 
              sx={{ 
                mt: 1, 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.15rem' }
              }}
            >
              {currencySymbol}{product.price || product.Price}
            </Typography>

            {hasSubProducts && (
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={t('hasSubproducts')} 
                  size="small" 
                  color="info"
                  icon={<CategoryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                  sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    height: { xs: 22, sm: 24 }
                  }}
                />
              </Box>
            )}
          </CardContent>

          <Box sx={{ 
            p: { xs: 0.8, sm: 1 }, 
            display: 'flex', 
            justifyContent: 'space-between', 
            borderTop: '1px solid', 
            borderColor: 'divider' 
          }}>
            {hasSubProducts ? (
              <Button 
                variant="text" 
                size="small" 
                onClick={() => navigate(`/admin/products/edit/${product.id || product.Id}`)}
                startIcon={<EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              >
                {isMobile ? t('edit') : t('editSubproducts')}
              </Button>
            ) : (
              <Box />
            )}
            
            <Box>
              {type === 'normal' && !hasSubProducts && (
                <Tooltip title={t('quickEdit')}>
                  <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={() => handleEditClick(product)}
                    sx={{ p: { xs: 0.5, sm: 1 } }}
                  >
                    <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('delete')}>
                <IconButton 
                  color="error" 
                  size="small" 
                  onClick={() => handleDelete(product.id || product.Id)}
                  sx={{ p: { xs: 0.5, sm: 1 } }}
                >
                  <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Card>
      </Grid>
    );
  };
    
  const HelpDialog = () => {
    const steps = [
      {
        label: t('helpProductsStep1Label') || 'Step 1: Search Products',
        icon: <SearchIcon color="primary" />,
        description: t('helpProductsStep1Description') || 'Use the search bar to quickly find products by name.',
        tips: [
          t('helpProductsStep1Tip1') || 'Type product name to filter results',
          t('helpProductsStep1Tip2') || 'Search works across all tabs',
          t('helpProductsStep1Tip3') || 'Results update instantly as you type'
        ]
      },
      {
        label: t('helpProductsStep2Label') || 'Step 2: Edit Products',
        icon: <EditIcon color="primary" />,
        description: t('helpProductsStep2Description') || 'Click the edit icon to modify product details.',
        tips: [
          t('helpProductsStep2Tip1') || 'Quick edit for simple changes',
          t('helpProductsStep2Tip2') || 'Multi-currency prices supported',
          t('helpProductsStep2Tip3') || 'Discount % updates all currencies',
          t('helpProductsStep2Tip4') || 'Changes save immediately'
        ]
      },
      {
        label: t('helpProductsStep3Label') || 'Step 3: Manage Images',
        icon: <CloudUploadIcon color="primary" />,
        description: t('helpProductsStep3Description') || 'Upload or change product images.',
        tips: [
          t('helpProductsStep3Tip1') || 'Supports JPG, PNG, WebP formats',
          t('helpProductsStep3Tip2') || 'Images auto-optimized for web',
          t('helpProductsStep3Tip3') || 'Preview before saving',
          t('helpProductsStep3Tip4') || 'Add up to 8 additional gallery images'
        ]
      },
      {
        label: t('helpProductsStep4Label') || 'Step 4: Delete Products',
        icon: <DeleteIcon color="error" />,
        description: t('helpProductsStep4Description') || 'Remove products permanently.',
        tips: [
          t('helpProductsStep4Tip1') || 'Confirm before deletion',
          t('helpProductsStep4Tip2') || 'Action cannot be undone',
          t('helpProductsStep4Tip3') || 'Related data will be removed'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('helpProductsDialogTitle') || 'Products Management Help'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setHelpOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              {t('helpWhatIsProductsPage') || 'What is this page?'}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('helpWhatIsProductsPageDesc') || 'This page allows you to view, edit, and manage all your products across different categories.'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      borderRadius: '50%', 
                      p: { xs: 1, sm: 1.5 }, 
                      display: 'flex',
                      boxShadow: 1
                    }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ 
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1.1rem' }
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold" 
                            color="primary" 
                            sx={{ 
                              mb: 1, 
                              display: 'block',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }
                            }}
                          >
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ 
                                fontSize: { xs: 14, sm: 16 }, 
                                mr: 1, 
                                color: 'success.main', 
                                mt: 0.3 
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                              >
                                {tip}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              <Box sx={{ mt: 1 }}>
                • {t('helpProductsImportantNote1') || 'Multi-currency prices are supported for normal products'}<br/>
                • {t('helpProductsImportantNote2') || 'Offers and grouped products: changing discount % updates ALL currencies'}<br/>
                • {t('helpProductsImportantNote3') || 'Products with subproducts require full edit mode'}<br/>
                • {t('helpProductsImportantNote4') || 'Add up to 8 additional gallery images per product'}<br/>
                • {t('helpProductsImportantNote5') || 'Always preview changes before saving'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setHelpOpen(false)} 
            variant="contained" 
            color="primary" 
            fullWidth={isMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress size={isMobile ? 50 : 60} />
    </Box>
  );

  const getCurrentProducts = () => {
    if (activeTab === 0) return filterProducts(normalProducts);
    if (activeTab === 1) return filterProducts(offers);
    return filterProducts(groupedProducts);
  };

  const currentProducts = getCurrentProducts();

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />

      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between" 
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <InventoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            {t('editAnyProduct')}
          </Typography>
        </Stack>
        <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
          <Button
            variant="contained"
            color="info"
            startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={() => setHelpOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {t('helpButtonText') || 'Help'}
          </Button>
        </Tooltip>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('selectBranchWarning') || 'Please select a branch first'}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder={t('searchProducts') || 'Search products...'}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: { xs: 20, sm: 24 } }} />
            </InputAdornment>
          )
        }}
        sx={{ 
          mb: { xs: 2.5, sm: 3 }, 
          maxWidth: { xs: '100%', sm: 500 },
          '& .MuiOutlinedInput-root': {
            borderRadius: { xs: 2, sm: 3 },
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }
        }}
      />

      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: { xs: 2.5, sm: 3 },
        overflowX: 'auto'
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              minHeight: { xs: 42, sm: 48 },
              px: { xs: 1.5, sm: 2 }
            }
          }}
        >
          <Tab 
            label={
              <Badge badgeContent={normalProducts.length} color="primary">
                <Box sx={{ px: { xs: 1, sm: 2 } }}>
                  {isMobile ? t('normalProducts') : (t('normalProducts') || 'Normal Products')}
                </Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={offers.length} color="warning">
                <Box sx={{ px: { xs: 1, sm: 2 } }}>
                  {isMobile ? t('offers') : (t('offers') || 'Offers')}
                </Box>
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={groupedProducts.length} color="info">
                <Box sx={{ px: { xs: 1, sm: 2 } }}>
                  {isMobile ? t('groupedProducts') : (t('groupedProducts') || 'Grouped Products')}
                </Box>
              </Badge>
            } 
          />
        </Tabs>
      </Box>

      {currentProducts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
          <InventoryIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'grey.300', mb: 2 }} />
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}
          >
            {searchQuery ? (t('noProductsMatchSearch') || 'No products match your search') : (t('noProductsFound') || 'No products found')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {currentProducts.map(p => <ProductCard key={p.id || p.Id} product={p} />)}
        </Grid>
      )}

      {/* ✅ EDIT DIALOG WITH MULTI-IMAGE + COLORS + SIZES SUPPORT */}
      <Dialog 
        open={editOpen} 
        onClose={() => {
          setEditOpen(false);
          setProductImages([]);
          setProductImagePreviews([]);
          setProductColors([]);
          setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
          setProductSizes([]); // ✅ NEW
          setCurrentSizeName(''); // ✅ NEW
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: { xs: 2, sm: 2.5 }
        }}>
          <Typography 
            variant="h6"
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {currentProduct?.Type === 'offer' ? (t('editingOffer') || 'Editing Offer') : 
             currentProduct?.Type === 'grouped' ? (t('editingGroupedProduct') || 'Editing Grouped Product') : 
             (t('quickEdit') || 'Quick Edit')}
          </Typography>
          <IconButton 
            onClick={() => {
              setEditOpen(false);
              setProductImages([]);
              setProductImagePreviews([]);
              setProductColors([]);
              setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
              setProductSizes([]);
              setCurrentSizeName('');
            }} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          {currentProduct && (
            <>
              {currentProduct.Type === 'offer' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                  >
                    ⚠️ {t('editingOfferWarning') || 'Editing offer product. Change discount % to update prices in all currencies.'}
                  </Typography>
                </Alert>
              )}
              {currentProduct.Type === 'grouped' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                  >
                    💰 {t('editingGroupedWarning') || 'Editing grouped product. Change discount % to update prices in all currencies.'}
                  </Typography>
                </Alert>
              )}
              {currentProduct.Type === 'normal' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                  >
                    💡 {t('normalProductNote') || 'Add or remove currencies as needed. At least one price is required.'}
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('productName') || 'Product Name'}
                    value={currentProduct.ProductName}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, ProductName: e.target.value })}
                    disabled={currentProduct.Type !== 'normal'}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }
                    }}
                  />
                </Grid>

                {(currentProduct.Type === 'offer' || currentProduct.Type === 'grouped') && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('discountPercentage') || 'Discount %'}
                        type="number"
                        value={currentProduct.Discount}
                        onChange={(e) => handlePriceChangeFromDiscount(e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('calculatedPrice') || 'Calculated Price'}
                        value={currentProduct.Price}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {getCurrencySymbol(currentProduct.Currency)}
                            </InputAdornment>
                          )
                        }}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        <Typography 
                          variant="body2" 
                          fontWeight="bold" 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                        >
                          💰 {t('pricesInAllCurrencies') || 'Prices in all currencies'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {Object.keys(currentProduct.CurrencyPrices || {}).map(currency => {
                            const oldPrice = currentProduct.OldCurrencyPrices?.[currency];
                            const newPrice = currentProduct.CurrencyPrices?.[currency];
                            const symbol = getCurrencySymbol(currency);
                            const currInfo = getCurrencyInfo(currency);
                            const decimals = currInfo.decimals || 2;
                            
                            const oldPriceNum = oldPrice != null ? parseFloat(oldPrice) : null;
                            const newPriceNum = newPrice != null ? parseFloat(newPrice) : null;
                            
                            const oldPriceStr = oldPriceNum !== null && !isNaN(oldPriceNum) 
                              ? oldPriceNum.toFixed(decimals) 
                              : '?';
                            const newPriceStr = newPriceNum !== null && !isNaN(newPriceNum) 
                              ? newPriceNum.toFixed(decimals) 
                              : '?';
                            
                            return (
                              <Chip
                                key={currency}
                                label={`${currency}: ${oldPriceStr} → ${newPriceStr} ${symbol}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            );
                          })}
                        </Box>
                      </Alert>
                    </Grid>
                  </>
                )}

                {currentProduct.Type === 'normal' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                      >
                        {t('prices') || 'Prices'} * ({Object.keys(currencyPrices).length} {t('currencies') || 'currencies'})
                      </Typography>
                    </Grid>

                    {Object.keys(currencyPrices).map((currency) => {
                      const currInfo = getCurrencyInfo(currency);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={currency}>
                          <Paper 
                            elevation={1} 
                            sx={{ 
                              p: { xs: 1.5, sm: 2 }, 
                              bgcolor: '#f9f9f9', 
                              borderLeft: '4px solid', 
                              borderColor: 'primary.main' 
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label={`${currInfo.symbol} ${currency}`}
                                color="primary"
                                size="small"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                              {Object.keys(currencyPrices).length > 1 && (
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemoveCurrency(currency)}
                                >
                                  <DeleteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                </IconButton>
                              )}
                            </Box>
                            <TextField
                              fullWidth
                              type="number"
                              value={currencyPrices[currency]}
                              onChange={(e) => handlePriceChange(currency, e.target.value)}
                              placeholder={`Price in ${currency}`}
                              inputProps={{ step: currInfo.decimals === 0 ? "1" : "0.01", min: "0" }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">{currInfo.symbol}</InputAdornment>
                                )
                              }}
                              size="small"
                              sx={{
                                '& .MuiInputBase-input': {
                                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                }
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                mt: 0.5, 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {currInfo.name}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}

                    <Grid item xs={12}>
                      <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                        <Select
                          value=""
                          onChange={(e) => handleAddCurrency(e.target.value)}
                          displayEmpty
                          startAdornment={
                            <InputAdornment position="start">
                              <AddIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
                            </InputAdornment>
                          }
                          sx={{
                            '& .MuiSelect-select': {
                              fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>➕ {t('addAnotherCurrency') || 'Add Another Currency'}</em>
                          </MenuItem>
                          {currencies
                            .filter(c => !currencyPrices[c.code])
                            .map((curr) => (
                              <MenuItem key={curr.code} value={curr.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    fontWeight="bold"
                                    sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                                  >
                                    {curr.symbol}
                                  </Typography>
                                  <Typography 
                                    variant="body2"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                                  >
                                    {curr.name} ({curr.code})
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('description') || 'Description'}
                        multiline
                        rows={isMobile ? 2 : 3}
                        value={currentProduct.Description}
                        onChange={(e) => setCurrentProduct({ ...currentProduct, Description: e.target.value })}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.85rem', sm: '0.9rem' }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('ingredients') || 'Ingredients'}
                        value={currentProduct.Ingredients}
                        onChange={(e) => setCurrentProduct({ ...currentProduct, Ingredients: e.target.value })}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.85rem', sm: '0.9rem' }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentProduct.IsActive}
                            onChange={(e) => setCurrentProduct({ ...currentProduct, IsActive: e.target.checked })}
                            size={isMobile ? "small" : "medium"}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                            {t('isActive') || 'Is Active'}
                          </Typography>
                        }
                      />
                    </Grid>
                  </>
                )}

                {/* MAIN IMAGE UPLOAD */}
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom 
                    fontWeight="bold"
                    sx={{ 
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1
                    }}
                  >
                    <CollectionsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    {t('mainProductImage') || 'Main Product Image'}
                  </Typography>
                  <Box sx={{ 
                    border: '2px dashed', 
                    borderColor: 'divider', 
                    p: { xs: 2, sm: 3 }, 
                    textAlign: 'center', 
                    borderRadius: 2, 
                    bgcolor: '#fafafa' 
                  }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="edit-image-file"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <label htmlFor="edit-image-file">
                      <Button 
                        variant="contained" 
                        component="span" 
                        startIcon={<CloudUploadIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                        size={isMobile ? "small" : "medium"}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                      >
                        {t('changeMainImage') || 'Change Main Image'}
                      </Button>
                    </label>
                    {imagePreview && (
                      <Box sx={{ mt: 2 }}>
                        <img 
                          src={imagePreview} 
                          alt="Main Preview" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: isMobile ? 200 : 300, 
                            borderRadius: 8,
                            border: '2px solid #e0e0e0'
                          }} 
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* ADDITIONAL IMAGES - ONLY FOR NORMAL PRODUCTS */}
                {currentProduct.Type === 'normal' && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      fontWeight="bold"
                      sx={{ 
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <CollectionsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      {t('additionalGalleryImages') || 'Additional Gallery Images'} ({productImagePreviews.length}/8)
                    </Typography>

                    {productImagePreviews.length > 0 && (
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {productImagePreviews.map((preview, index) => (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Paper 
                              elevation={2}
                              sx={{ 
                                position: 'relative',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '2px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <img 
                                src={preview} 
                                alt={`Gallery ${index + 1}`}
                                style={{ 
                                  width: '100%', 
                                  height: isMobile ? 100 : 150, 
                                  objectFit: 'cover' 
                                }} 
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveProductImage(index)}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'error.main',
                                  color: 'white',
                                  width: { xs: 24, sm: 28 },
                                  height: { xs: 24, sm: 28 },
                                  '&:hover': {
                                    bgcolor: 'error.dark'
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              </IconButton>
                              <Chip
                                label={`${index + 1}`}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  bottom: 4,
                                  left: 4,
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {productImagePreviews.length < 8 && (
                      <Box sx={{ 
                        border: '2px dashed', 
                        borderColor: 'primary.main', 
                        p: { xs: 2, sm: 3 }, 
                        textAlign: 'center', 
                        borderRadius: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05) 
                      }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="product-images-file"
                          type="file"
                          multiple
                          onChange={handleProductImagesChange}
                        />
                        <label htmlFor="product-images-file">
                          <Button 
                            variant="outlined" 
                            component="span" 
                            color="primary"
                            startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                            size={isMobile ? "small" : "medium"}
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                          >
                            {t('addGalleryImages') || 'Add Gallery Images'} ({8 - productImagePreviews.length} {t('remaining') || 'remaining'})
                          </Button>
                        </label>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            mt: 1, 
                            color: 'text.secondary',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}
                        >
                          💡 {t('multipleImagesInfo') || 'You can select multiple images at once (up to 8 total)'}
                        </Typography>
                      </Box>
                    )}

                    {productImagePreviews.length >= 8 && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                          ✓ {t('maxImagesReached') || 'Maximum 8 images reached. Remove an image to add new ones.'}
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                )}

                {/* Product Colors Section - ONLY FOR NORMAL PRODUCTS */}
                {currentProduct.Type === 'normal' && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        🎨 {t('productColors') || 'Product Colors'}
                      </Typography>
                      <Chip 
                        label={`${productColors.length} ${t('colors') || 'colors'}`}
                        size="small"
                        color="secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                      <Chip 
                        label={t('optional') || 'Optional'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </Stack>

                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: { xs: 2, sm: 2.5 }, 
                        mb: 2, 
                        bgcolor: alpha(theme.palette.secondary.main, 0.05),
                        border: `2px dashed ${theme.palette.secondary.main}`,
                        borderRadius: 2
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        fontWeight="bold" 
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, mb: 1, display: 'block' }}
                      >
                        ➕ {t('addNewColor') || 'Add New Color'}
                      </Typography>
                      
                      <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            value={currentColor.colorName}
                            onChange={handleColorNameChange}
                            placeholder={t('enterColorName') || 'e.g., Red, Blue, Black'}
                            variant="outlined"
                            size="small"
                            sx={{
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '0.85rem', sm: '0.9rem' }
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="edit-color-image-upload"
                            type="file"
                            onChange={handleColorImageChange}
                          />
                          <label htmlFor="edit-color-image-upload">
                            <Button 
                              variant="outlined" 
                              component="span" 
                              fullWidth
                              size="small"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                            >
                              {currentColor.colorImagePreview 
                                ? `✓ ${t('imageSelected') || 'Image'}` 
                                : t('uploadImage') || 'Upload'}
                            </Button>
                          </label>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddColor}
                            fullWidth
                            size="small"
                            disabled={!currentColor.colorName.trim()}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                          >
                            {t('add') || 'Add'}
                          </Button>
                        </Grid>

                        {currentColor.colorImagePreview && (
                          <Grid item xs={12}>
                            <Paper 
                              elevation={1}
                              sx={{ 
                                maxWidth: 150, 
                                overflow: 'hidden', 
                                borderRadius: 2 
                              }}
                            >
                              <img 
                                src={currentColor.colorImagePreview} 
                                alt="Preview"
                                style={{ 
                                  width: '100%', 
                                  height: 80, 
                                  objectFit: 'cover' 
                                }} 
                              />
                            </Paper>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>

                    {productColors.length > 0 && (
                      <Box>
                        <Typography 
                          variant="caption" 
                          fontWeight="bold" 
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, mb: 1, display: 'block' }}
                        >
                          📋 {t('addedColors') || 'Added Colors'} ({productColors.length})
                        </Typography>
                        
                        <Grid container spacing={1}>
                          {productColors.map((color, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <Card 
                                sx={{ 
                                  position: 'relative',
                                  border: `2px solid ${theme.palette.secondary.main}`,
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}
                              >
                                <IconButton
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    zIndex: 1,
                                    width: 24,
                                    height: 24,
                                    '&:hover': { bgcolor: 'error.dark' }
                                  }}
                                  onClick={() => handleRemoveColor(index)}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>

                                {color.colorImagePreview ? (
                                  <CardMedia
                                    component="img"
                                    image={color.colorImagePreview}
                                    alt={color.colorName}
                                    sx={{ height: { xs: 80, sm: 100 }, objectFit: 'cover' }}
                                  />
                                ) : (
                                  <Box 
                                    sx={{ 
                                      height: { xs: 80, sm: 100 }, 
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        fontSize: { xs: '1.5rem', sm: '2rem' },
                                        color: theme.palette.secondary.main 
                                      }}
                                    >
                                      🎨
                                    </Typography>
                                  </Box>
                                )}

                                <CardContent sx={{ p: 1, bgcolor: 'secondary.main' }}>
                                  <Typography 
                                    variant="caption" 
                                    fontWeight="bold" 
                                    align="center"
                                    sx={{ 
                                      color: 'white',
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {color.colorName}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                    
                    {productColors.length === 0 && (
                      <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                        ℹ️ {t('noColorsAdded') || 'No colors added yet. Add colors above to give customers options.'}
                      </Alert>
                    )}
                  </Grid>
                )}

                {/* ✅ NEW: Product Sizes Section - ONLY FOR NORMAL PRODUCTS */}
                {currentProduct.Type === 'normal' && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        📏 {t('productSizes') || 'Product Sizes'}
                      </Typography>
                      <Chip 
                        label={`${productSizes.length} ${t('sizes') || 'sizes'}`}
                        size="small"
                        sx={{ 
                          bgcolor: '#ed6c02',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' } 
                        }}
                      />
                      <Chip 
                        label={t('optional') || 'Optional'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </Stack>

                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block', 
                        mb: 2, 
                        fontSize: { xs: '0.75rem', sm: '0.8rem' } 
                      }}
                    >
                      💡 {t('sizesHint') || 'Add size options for this product (e.g., S, M, L, XL or 39, 40, 41).'}
                    </Typography>

                    {/* Add New Size Form */}
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: { xs: 2, sm: 2.5 }, 
                        mb: 2, 
                        bgcolor: alpha('#ed6c02', 0.05),
                        border: '2px dashed #ed6c02',
                        borderRadius: 2
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        fontWeight="bold" 
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, mb: 1.5, display: 'block' }}
                      >
                        ➕ {t('addNewSize') || 'Add New Size'}
                      </Typography>
                      
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <TextField
                          fullWidth
                          value={currentSizeName}
                          onChange={(e) => setCurrentSizeName(e.target.value)}
                          placeholder={t('enterSizeName') || 'e.g., S, M, L, XL'}
                          variant="outlined"
                          size="small"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddSize();
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.85rem', sm: '0.9rem' }
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleAddSize}
                          disabled={!currentSizeName.trim()}
                          size="small"
                          sx={{ 
                            bgcolor: '#ed6c02',
                            '&:hover': { bgcolor: '#e65100' },
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            minWidth: { xs: 70, sm: 90 },
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {t('addSize') || 'Add'}
                        </Button>
                      </Stack>
                    </Paper>

                    {/* Display Added Sizes as Chips */}
                    {productSizes.length > 0 && (
                      <Box>
                        <Typography 
                          variant="caption" 
                          fontWeight="bold" 
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, mb: 1, display: 'block' }}
                        >
                          📋 {t('addedSizes') || 'Added Sizes'} ({productSizes.length})
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {productSizes.map((size, index) => (
                            <Chip
                              key={index}
                              label={size.sizeName}
                              onDelete={() => handleRemoveSize(index)}
                              deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                bgcolor: '#ed6c02',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                height: { xs: 32, sm: 36 },
                                '& .MuiChip-deleteIcon': {
                                  color: 'rgba(255,255,255,0.7)',
                                  '&:hover': {
                                    color: 'white'
                                  }
                                },
                                boxShadow: '0 2px 4px rgba(237,108,2,0.3)',
                                '&:hover': {
                                  bgcolor: '#e65100'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {productSizes.length === 0 && (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          '& .MuiAlert-icon': {
                            fontSize: { xs: 18, sm: 22 }
                          }
                        }}
                      >
                        ℹ️ {t('noSizesAdded') || 'No sizes added yet. Add sizes above to give customers size options.'}
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </>
          )}
          
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 2.5 }, 
          bgcolor: '#f9f9f9',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => {
              setEditOpen(false);
              setProductImages([]);
              setProductImagePreviews([]);
              setProductColors([]);
              setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
              setProductSizes([]); // ✅ NEW
              setCurrentSizeName(''); // ✅ NEW
            }} 
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('updateProduct') || 'Update Product'}
          </Button>
          <Button 
            onClick={() => { 
              setEditOpen(false); 
              setProductImages([]);
              setProductImagePreviews([]);
              setProductColors([]);
              setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
              setProductSizes([]); // ✅ NEW
              setCurrentSizeName(''); // ✅ NEW
              handleDelete(currentProduct.Id); 
            }} 
            variant="contained"
            color="error"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;